
'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import {
  Keyboard, Mousewheel, Navigation, Virtual,
} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import {
  ArrowDownIcon, ArrowUpIcon, SpeakerWaveIcon, SpeakerXMarkIcon,
} from '@heroicons/react/16/solid';
import {
  Avatar, LinkStyled, Modal,
} from '@components/common';
import { useTranslation } from 'next-i18next';
import { useSwrStoriesFetchAll } from '@hooks/useSwrApi/endpoints/stories/useSwrStoriesFetchAll';
import {
  ReactNode, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  PauseIcon, PlayIcon, ShareIcon,
} from '@heroicons/react/20/solid';
import Confetti from 'react-confetti-boom';
import { useAuthClient } from '@root/src/hooks';
import { CvDetailHub } from '@components/templates/cv/CvDetailHub';
import { ShareModal } from '@components/features/modals/share-modal/ShareModal';
import { getI18nLinkHref } from '@services/i18nPaths';
import { trackEvent } from '@services/tracking';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { useSwrStoriesDeleteDislike } from '@hooks/useSwrApi/endpoints/stories/useSwrStoriesDeleteDislike';
import { useSwrStoriesDislike } from '@hooks/useSwrApi/endpoints/stories/useSwrStoriesDislike';
import { FlagIcon as FlagSolid } from '@heroicons/react/24/solid';
import { FlagIcon as FlagOutline } from '@heroicons/react/24/outline';
import { StoriesActionIcons } from '@components/templates/stories/StoriesActionIcons';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { useResumeAccessCheck } from '@components/templates/stories/hooks/useResumeAccesCheck';
import { StoriesAnswerPromptBar } from './StoriesAnswerPromptBar';
import {
  attachHls, findHlsStreamUrl, getHlsSupport, type HlsSupport,
} from '@utils/video/hlsPlayback';
import { trackPlaybackQuality } from '@utils/video/playbackQuality';


interface SwiperStoriesCardsProps {
  initialData?: ApiSchema<'ResumeStoriesResult'>;
  locale: Locale;
  ps?: string;
  storyRoute?: string;
  syncUrl?: boolean;
  /** Replaces the interview-answer prompt bar at the bottom (e.g. a talent-search bar). */
  bottomSlot?: ReactNode;
  /** Shows a link to the viewer's own interview questions under each card's question — the
   *  talent-search feed's only candidate-facing entry point. Off on /stories. */
  showMyInterviewsCta?: boolean;
}

export default function SwiperStoriesCards({
  initialData, locale, ps, storyRoute = '/stories', syncUrl = true, bottomSlot, showMyInterviewsCta = false,
}: SwiperStoriesCardsProps) {
  const { t } = useTranslation('common');
  const headerInfoData = useAppSelector(getUserHeaderInfo);
  const userId = headerInfoData?.userId;
  const videoRefs = useRef<HTMLVideoElement[]>([]);
  const [swiperIndex, setSwiperIndex] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isOpenShareModal, toggleShareModal] = useState(false);
  const { isAuth } = useAuthClient();
  const { withLocale } = useExtendedRouter();

  const [selectedCvSlug, setSelectedCvSlug] = useState<string | null>(null);
  const [muted, setMuted] = useState(true);
  const [hasInteracted, setHasinteracted] = useState(false);
  const [showIcon, setShowIcon] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const iconTimeout = useRef<NodeJS.Timeout | null>(null);
  const progressRefs = useRef<HTMLDivElement[]>([]);


  const {
    data, size, setSize, isValidating,
  } = useSwrStoriesFetchAll({
    locale,
    prioritisedSlug: ps,
    serverSideData: initialData,
  });


  const totalPages = initialData?.pages ?? Infinity;
  const items = useMemo(
    () => data?.flatMap((page) => page.items || []) || [],
    [data],
  );
  const hasTrackedInitialView = useRef(false);

  const activeVideoItem = items[swiperIndex];
  const activeVideoSlug = activeVideoItem?.slug;

  // HLS capability is probed after mount only — SSR/first client render must agree on the
  // MP4 fallback markup, then capable browsers upgrade to adaptive streams.
  const [hlsSupport, setHlsSupport] = useState<HlsSupport>({ native: false, mse: false });
  useEffect(() => {
    setHlsSupport(getHlsSupport());
  }, []);

  const activeHlsUrl = findHlsStreamUrl(activeVideoItem?.videos?.[0]?.streams);
  const activeMp4Url = activeVideoItem?.videos?.[0]?.url;

  // MSE browsers (Chrome/Firefox/Android) play HLS through hls.js attached to the ACTIVE
  // video only; its JSX src stays undefined so React and hls.js don't fight over the
  // attribute. Only MSE-less browsers (iPhone Safari) get the manifest as plain src —
  // canPlayType alone lies (desktop Chrome says "maybe" but can't play HLS natively).
  useEffect(() => {
    const video = videoRefs.current[swiperIndex];
    if (!video || !activeHlsUrl || !hlsSupport.mse) return undefined;
    return attachHls(video, activeHlsUrl, { autoplay: true, fallbackUrl: activeMp4Url });
  }, [swiperIndex, activeHlsUrl, activeMp4Url, hlsSupport]);

  // Field metrics per watched video (startup latency, stalls, dropped frames) — users
  // report desync we can't reproduce; GA shows whether the playback changes help.
  useEffect(() => {
    const video = videoRefs.current[swiperIndex];
    if (!video || !activeVideoSlug) return undefined;
    const usesHls = Boolean(activeHlsUrl) && (hlsSupport.native || hlsSupport.mse);
    const finalize = trackPlaybackQuality(video, { slug: activeVideoSlug, source: usesHls ? 'hls' : 'mp4' });
    window.addEventListener('pagehide', finalize);
    return () => {
      window.removeEventListener('pagehide', finalize);
      finalize();
    };
  }, [swiperIndex, activeVideoSlug, activeHlsUrl, hlsSupport]);

  const { isChecking: isLoadingSecurityCheck, isAllowed: isAllowedPlayingVideo, check: checkResumeAccess } = useResumeAccessCheck(locale, activeVideoSlug);

  useEffect(() => {
    if (!activeVideoSlug) {
      // Nothing loaded yet — nothing to play.
      videoRefs.current.forEach((v) => v?.pause());
      return;
    }

    void checkResumeAccess(activeVideoSlug);
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === swiperIndex) v.play().catch(() => {});
      else {
        v.pause();
        v.currentTime = 0;
        // Left the ±1 src window: React only removes the src attribute, which does not
        // reliably abort the in-flight download — load() resets the element so the browser
        // cancels the fetch and releases the decoder.
        if (!v.getAttribute('src') && v.readyState !== 0) v.load();
      }
    });
  }, [activeVideoSlug, checkResumeAccess, swiperIndex]);


  // Track initial story view when stories are first loaded
  useEffect(() => {
    if (items && items.length > 0 && !hasTrackedInitialView.current) {
      sendGoogleTagEvent('viewed_interview', 'Custom', { source: 'stories_page' });
      hasTrackedInitialView.current = true;
    }
  }, [items]);

  const { trigger: triggerStoriesDislike } = useSwrStoriesDislike({
    slug: activeVideoSlug ?? '',
    successCallback: () => {
      if (activeVideoItem) activeVideoItem.dislikedByMe = true;
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 4000);
    },
  });

  const { trigger: triggerDeleteStoriesDislike } = useSwrStoriesDeleteDislike({
    slug: activeVideoSlug ?? '',
    successCallback: () => {
      if (activeVideoItem) activeVideoItem.dislikedByMe = false;
    },
  });

  const toggleMuteAll = useCallback((value?: string) => {
    if (!value) return;

    if (value === 'true') {
      setMuted(true);
    } else if (value === 'false') {
      setMuted(false);
    } else if (value === 'prev') {
      setMuted((prev) => !prev);
    }
  }, []);


  const updateVideos = (activeIndex: number) => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i === activeIndex) {
        if (isAllowedPlayingVideo && !isLoadingSecurityCheck) {
          void video.play().catch(() => {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  };


  const handleStoriesStart = (index:number) => {
    const video = videoRefs.current[index];
    if (!video) return;
    video.play().catch(() => {});
    toggleMuteAll('false');
    setHasinteracted(true);
  };

  const pauseVideo = useCallback((index: number) => {
    const video = videoRefs.current[index];
    if (!video || video.paused) return;

    video.pause();
    setIsPlaying(false);
  }, []);

  const handleVideoClick = useCallback(
    (index: number) => {
      if (!hasInteracted) return;

      const video = videoRefs.current[index];
      if (!video) return;

      if (video.paused) {
        video.play().catch(() => {});
        setIsPlaying(true);
        toggleMuteAll('false');
      } else {
        video.pause();
        setIsPlaying(false);
        toggleMuteAll('true');
      }

      // Show icon for 1 second
      setShowIcon(true);
      if (iconTimeout.current) clearTimeout(iconTimeout.current);
      iconTimeout.current = setTimeout(() => setShowIcon(false), 1000);
    },
    [hasInteracted, toggleMuteAll],
  );

  useEffect(() => {
    if (!syncUrl) return;
    if (!activeVideoSlug) return;

    window.history.pushState(
      null,
      '',
      withLocale(`${storyRoute}?ps=${activeVideoSlug}`),
    );
  }, [activeVideoSlug, storyRoute, syncUrl, withLocale]);

  // Question the bottom bar should answer: the current video card's linked question (as long
  // as it's not the viewer's own card). Shown even if already answered — resubmitting just
  // upserts the existing answer, no need to hide the bar.
  const activeVideoQuestion = activeVideoItem?.videos?.[0]?.question;
  const barQuestion = (isAuth && !!activeVideoQuestion && activeVideoItem?.slug !== headerInfoData?.userSlug)
    ? activeVideoQuestion
    : undefined;

  return (
    <>
      {/* Flex column pinned to the viewport (minus the fixed header, and on mobile the fixed
          BottomNav) so the card never pushes the page taller than the available viewport — an
          overflow here would let the page scroll, which desyncs from the swiper's own
          card-to-card navigation. */}
      <div className="flex h-[calc(100dvh-var(--header-height,4rem)-3.5rem-env(safe-area-inset-bottom))] flex-col lg:h-[calc(100dvh-var(--header-height,4rem))]">
        <div
          // Bottom margin just big enough to clear the fixed prompt bar: pill (~52px) + mt-2 gap
          // (8px) + the bar's own bottom offset (bottom-3/lg:bottom-8).
          className="relative mb-[calc(8rem+env(safe-area-inset-bottom))] min-h-0 flex-1 max-md:px-3 md:container md:w-125 lg:mb-[calc(7rem+env(safe-area-inset-bottom))] xl:w-200"
        >
      <ShareModal
        isOpen={isOpenShareModal}
        onClose={() => toggleShareModal(false)}
        ogTitle={t('meta.stories.share.modal.title')}
        ogDescription={t('meta.stories.share.modal.title')}
        pathnameShareUrl={`${getI18nLinkHref(`/stories?ps=${activeVideoSlug}`, locale)}`}
      />

      {selectedCvSlug && (
        <Modal
          isOpen
          onClose={() => {
            setSelectedCvSlug(null);
          }}
          size="full"
          className="transform-none!"
          body={(
            <>
              <CvDetailHub
                trackingData={{
                  listType: 'stories',
                }}
                slug={selectedCvSlug}
                isAuthenticated={isAuth}
                closeModal={() => {
                  setSelectedCvSlug(null);
                }}
              />
            </>
          )}
        />
      )}

      <Swiper
        direction="vertical"
        modules={[Navigation, Mousewheel, Keyboard, Virtual]}
        // Only the active slide ± neighbors stay mounted — without this the DOM grows by a
        // full card (video element included) per fetched story and never shrinks.
        virtual
        keyboard={{ enabled: true, onlyInViewport: false }}
        // Trackpads fire a burst of wheel events per swipe gesture; without a time/delta
        // threshold that burst can cross the "advance one slide" threshold twice, skipping
        // a card. thresholdTime throttles how often a single gesture can trigger a change.
        mousewheel={{ thresholdDelta: 30, thresholdTime: 400, forceToAxis: true }}
        navigation={{ nextEl: '.next-btn', prevEl: '.prev-btn' }}
        slidesPerView={1}
        observeParents
        observer
        className="h-full"
        onSlideChange={(swiper) => {
          const index = swiper.activeIndex;
          setHasinteracted(true);
          setSwiperIndex(index);
          const item = items[index];
          if (!item) return;
          updateVideos(index);

          trackEvent({
            eventType: 'view',
            listType: 'stories',
            entityType: 'resume',
            entityId: item.slug ?? '',
            viewContext: 'detail',
            ...(userId && { userId: userId.toString() }),
            metadata: [
              { key: 'video', value: item.slug ?? '' },
            ],
          });
          sendGoogleTagEvent('viewed_interview', 'Custom', { source: 'stories_page' });

          // Fetch next page if near the end of the feed.
          if (!isValidating && size < totalPages && index >= items.length - 2) {
            void setSize(size + 1);
          }
        }}
      >
        {items.map((item, index) => {
          const firstVideo = item.videos?.[0];
          if (!firstVideo) return null;

          const hlsUrl = findHlsStreamUrl(firstVideo.streams);
          const inSrcWindow = Math.abs(index - swiperIndex) <= 1;
          // Native HLS (Safari/iOS): manifest goes straight into src, neighbors included
          // (preload=metadata keeps their cost tiny). MSE browsers: the ACTIVE video is fed
          // by hls.js (src stays undefined); neighbors preload MP4 metadata as before.
          const playsHlsNatively = Boolean(hlsUrl) && hlsSupport.native && !hlsSupport.mse;
          const playsHlsViaMse = Boolean(hlsUrl) && hlsSupport.mse;
          let videoSrc: string | undefined;
          if (inSrcWindow) {
            if (playsHlsNatively) videoSrc = hlsUrl;
            else if (!(playsHlsViaMse && index === swiperIndex)) videoSrc = firstVideo.url;
          }

          return (
            <SwiperSlide key={`${item.id}-${firstVideo.id ?? index}`} virtualIndex={index}>
              <div className="flex size-full flex-col">
              <div className="relative min-h-0 flex-1 overflow-hidden rounded-3xl! border-y-8 border-surface-100">

                {(isLoadingSecurityCheck || !isAllowedPlayingVideo)
                  && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black opacity-100">
                      <div className="flex items-center space-x-3">
                        <div className="size-4 animate-bounce rounded-full bg-secondary" />
                        <div className="size-4 animate-bounce rounded-full bg-white [animation-delay:0.15s]" />
                        <div className="size-4 animate-bounce rounded-full bg-secondary [animation-delay:0.3s]" />
                      </div>

                      {/* We need server side check here */}
                      {!isAuth
                        && (
                        <LinkStyled
                          href="/login"
                          styling="solid"
                          size="sm"
                          color="secondary"
                          rounding="full"
                          className="mt-3"
                          aria-label={t('login.meta.title')}
                        >
                          {t('login.meta.title')}
                        </LinkStyled>
                        )}
                    </div>
                  )}

                {/* Interaction buttons */}
                <div className="absolute right-4 top-8 z-2 flex flex-col items-center gap-5">

                  {/* Mute / Unmute */}
                  <StoriesActionIcons
                    onClick={() => toggleMuteAll('prev')}
                    icon={muted ? <SpeakerXMarkIcon className="size-5" /> : <SpeakerWaveIcon className="size-5" />}
                  />

                  {/* Share */}
                  <StoriesActionIcons
                    onClick={() => toggleShareModal(true)}
                    icon={<ShareIcon className="size-5" />}
                  />

                  <StoriesActionIcons
                    onClick={!isAuth ? undefined : () => {
                      void (item.dislikedByMe ? triggerDeleteStoriesDislike() : triggerStoriesDislike());
                    }}
                    isLink={!isAuth}
                    href={!isAuth ? '/login' : undefined}
                    icon={item.dislikedByMe ? <FlagSolid className="size-5 text-ink" />
                      : <FlagOutline className="size-5 text-ink" />}
                  />

                </div>


                <div
                  role="presentation"
                  className="absolute left-0 top-0 z-1 h-1 w-full cursor-pointer bg-gray-400"
                  onClick={(e) => {
                    const activeVideo = videoRefs.current[swiperIndex];
                    if (!activeVideo) return;

                    const rect = (e.target as HTMLElement).getBoundingClientRect();
                    const clickX = e.clientX - rect.left;
                    activeVideo.currentTime = (clickX / rect.width) * activeVideo.duration;
                  }}
                >
                  <div
                    role="presentation"
                    className="relative h-1 w-full cursor-pointer bg-gray-400"
                    onClick={(e) => {
                      const video = videoRefs.current[swiperIndex];
                      if (!video) return;

                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      const clickX = e.clientX - rect.left;
                      video.currentTime = (clickX / rect.width) * video.duration;
                    }}
                  >
                    <div
                      ref={(el) => {
                        if (el) progressRefs.current[index] = el;
                      }}
                      className="h-1 bg-secondary transition-all duration-200 ease-linear"
                    />
                  </div>
                </div>
                <video
                  ref={(el) => {
                    if (el) videoRefs.current[index] = el;
                  }}
                  src={videoSrc}
                  // Only the active video buffers fully; neighbors fetch just metadata so a
                  // single decoder/download runs at a time (3 parallel full fetches starved
                  // slow devices — CPU spikes dropped audio frames out of sync).
                  preload={index === swiperIndex ? 'auto' : 'metadata'}
                  poster={firstVideo.thumbnailUrl ?? undefined}
                  className="size-full bg-[#000000] object-cover md:object-contain"
                  loop
                  onClick={() => handleVideoClick(index)}
                  muted={muted}
                  onTimeUpdate={(e) => {
                    const video = e.currentTarget;
                    const progressEl = progressRefs.current[index];
                    if (!progressEl) return;
                    progressEl.style.width = `${(video.currentTime / video.duration) * 100}%`;
                  }}
                  playsInline
                />

                {!hasInteracted
                  && (
                    <div
                      role="presentation"
                      onClick={() => handleStoriesStart(index)}
                      className="absolute inset-0 z-1 flex flex-col items-center justify-center bg-dark/30"
                    >
                      <div
                        className="flex size-16 items-center justify-center rounded-full bg-black/60"
                      >
                        <PlayIcon className="size-10 text-white opacity-90" />
                      </div>
                    </div>

                  )}

                {/* Overlay Icon */}
                {showIcon && (
                  <div
                    className="pointer-events-none absolute left-1/2 top-1/2 flex size-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60"
                  >
                    {isPlaying ? (
                      <PlayIcon className="size-10 animate-fade text-white opacity-90" />
                    ) : (
                      <PauseIcon className="size-10 animate-fade text-white opacity-90" />
                    )}
                  </div>
                )}

                {showConfetti && (
                  <Confetti />
                )}

                <div
                  className="absolute top-1 flex h-25 w-full items-center justify-start bg-story-top  p-3 text-white"
                >

                  <button
                    type="button"
                    onClick={() => {
                      pauseVideo(index);
                      setSelectedCvSlug(item.slug ?? '');
                    }}
                    className="flex items-center justify-start gap-2 text-left transition hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                    aria-label={t('stories.view.profile', 'View profile')}
                  >
                    {item.imgUrl && (
                      <div className="min-w-14">
                        <Avatar
                          size="sm"
                          imgPath={item.imgUrl}
                          aspectRatio="square-uncertain"
                          extractImageName
                          alt={item.slug}
                          className="mx-auto"
                        />
                      </div>
                    )}
                    {item.leadExperience && (
                      <div className="pr-10 md:pr-0">
                        <div className="max-w-81.25 overflow-hidden truncate whitespace-nowrap text-sm font-bold text-white md:text-lg">
                          {item.leadExperience?.title}
                        </div>
                        <div className="mb-2 text-xs text-white">
                          @
                          {item.leadExperience?.company}
                        </div>
                      </div>
                    )}
                  </button>
                </div>
              </div>

              {/* Question moved below the card (not overlaid on the video) so it reads as
                  "here's what to answer", not a caption on someone else's video. */}
              <div className="shrink-0 px-3 pb-10 pt-5 text-center text-lg font-medium text-ink md:pb-16 md:text-xl">
                {firstVideo.question}
                {showMyInterviewsCta && (
                  <LinkStyled
                    href="/my-interview-questions"
                    styling="none"
                    className="mt-2 block text-sm font-normal text-primary underline"
                  >
                    {t('my.interviews.cta')}
                  </LinkStyled>
                )}
              </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
      {/* Custom navigation buttons */}
      <div className="absolute -right-16 top-1/2 z-2 -mt-12 hidden flex-col gap-4 md:flex">
        <button type="button" className="prev-btn rounded-full bg-surface p-2 text-ink">
          <ArrowUpIcon className="size-5" />
        </button>
        <button type="button" className="next-btn rounded-full bg-surface p-2 text-ink">
          <ArrowDownIcon className="size-5" />
        </button>
      </div>

      </div>
      </div>

      {/* Bottom prompt bar — always visible. Default: the interview-answer bar scoped to the
          current slide's question. `bottomSlot` swaps it out (e.g. mobile talent search). */}
      {bottomSlot ?? (
        <StoriesAnswerPromptBar
          question={barQuestion}
          locale={locale}
          isAuthenticated={isAuth}
          firstMessage={t('audio-interview.bestie.message')}
        />
      )}
    </>
  );
}
